import { receiptId } from "@/assets/googleTrackIds";
import type { TransferOperationItemModel } from "@payping/api/app/api/digitalAccountSamanTypes";
import {
  Button,
  Copy,
  Flex,
  Page,
  Table,
  Tag,
  Typography,
  useMessagedAsyncFn,
  useMessagedAsyncRetry,
} from "@payping/web-components";
import { SubLayout } from "@/components/SubLayout";
import { useTranslation } from "@payping/utils/localeUtils";
import type { ColumnsType } from "@payping/web-components/components/table/types";
import { ReactNode, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SamanApis, TransferStateEnum } from "@payping/api";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { dateFormats, toJalali } from "@payping/utils/dateUtils";
import { getTransferRequestList } from "@payping/redux/actions";
import { useAppDispatch, useAppSelector } from "@/utils";
import { submitQueryParams } from "@payping/utils/query-string";
import qs from "query-string";
import { ListNumberQueryParamsType } from "@/pages/DigitalAccount/Saman/requestType";
import { CuToman } from "@payping/icons";

const ShowAllRequestTransferList = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { transferRequestList } = useAppSelector((state) => state.digitalAccount.saman);
  const navigate = useNavigate();
  const location = useLocation();
  const parsedSearch = qs.parse(location.search);
  const { pageNumber = 1, pageSize = 10 }: ListNumberQueryParamsType = qs.parse(location.search, {
    parseNumbers: true,
  });
  const [downloadReceipt, setDownloadReceipt] = useState<string>();

  const { loading, retry } = useMessagedAsyncRetry(async () => {
    await dispatch(
      getTransferRequestList({
        pageNumber,
        pageSize,
      }),
    );
  }, [dispatch, pageSize, pageNumber]);

  const [{ loading: downloadReceiptLoading }, downloadTransferReceipt] = useMessagedAsyncFn(async (row) => {
    setDownloadReceipt(row.id);
    const newWindow = window.open(undefined, "_blank");
    const res = await SamanApis.getTransferReceiptWithRequestModelWithCancellationTokenGet(row.id);
    newWindow?.location?.assign?.(res.path ?? "");
  }, []);

  const dataTransferTable = transferRequestList.items?.map((item, index) => ({ ...item, key: index }));

  const cols: ColumnsType<TransferOperationItemModel> = [
    {
      dataIndex: "date",
      render(_, tableData) {
        return (
          <div className={"justify-start"}>
            <div style={{ direction: "ltr" }}>
              {
                toJalali({
                  date: tableData.operationCreationDateTime,
                  dateFormat: dateFormats.standard.utcDateTime,
                  toFormat: dateFormats.ui.jalaliDateTime,
                }).string
              }
            </div>
          </div>
        );
      },
      title: t("digitalAccount.samanTurnOver.table.date"),
    },
    {
      dataIndex: "amount",
      render(_, tableData) {
        return (
          <Flex align="center">
            <div>{`${formatLocalizedNumber({ value: Math.trunc((tableData.amount || 0) / 10) })}`}</div>
            <CuToman width={24} height={24} />
          </Flex>
        );
      },
      title: t("digitalAccount.samanRequestList.table.amount"),
    },
    {
      dataIndex: "state",
      key: "state",
      render(value) {
        const tagColor = () => {
          switch (value) {
            case TransferStateEnum.Accepted:
              return "orange";
            case TransferStateEnum.Created:
              return "orange";
            case TransferStateEnum.Transferred:
              return "green";
            case TransferStateEnum.Rejected:
              return "red";
            default:
              return undefined;
          }
        };
        return (
          <Tag color={tagColor()}>{t(`digitalAccount.samanRequestList.table.status.${TransferStateEnum[value]}`)}</Tag>
        );
      },
      title: t("digitalAccount.samanRequestList.table.state"),
    },
    {
      dataIndex: "ownerName",
      render(_, tableData) {
        return <Typography.Text>{tableData.destination?.ownerName}</Typography.Text>;
      },
      title: t("digitalAccount.samanRequestList.table.destination"),
    },
    {
      dataIndex: "iban",
      render(_, tableData) {
        return <Copy text={tableData.destination?.iban || ""}>{tableData.destination?.iban}</Copy>;
      },
      title: t("digitalAccount.samanRequestList.table.iban"),
    },
  ];

  const actions = (_: number, record?: TransferOperationItemModel): ReactNode[] => {
    return [
      Number(record?.state) === TransferStateEnum.Transferred ? (
        <Button
          key="report"
          onClick={() => downloadTransferReceipt(record)}
          type="link"
          loading={downloadReceiptLoading && downloadReceipt === record?.id}
          className="inline"
          data-google-track={receiptId}
        >
          {t("digitalAccount.samanRequestList.table.getReceipt")}
        </Button>
      ) : (
        <Typography.Text disabled>{t("digitalAccount.samanRequestList.table.getReceipt")}</Typography.Text>
      ),
    ];
  };

  return (
    <Page>
      <SubLayout>
        <Flex vertical gap={"middle"}>
          <Typography.Title level={4}>{t("digitalAccount.samanRequestList.title")}</Typography.Title>

          <Table<TransferOperationItemModel>
            actions={actions}
            loading={loading}
            hasIdColumn
            dataSource={dataTransferTable}
            columns={cols}
            pagination={{
              onChange: (page, newSize): void => {
                const newValues = {
                  ...parsedSearch,
                  pageNumber: page,
                  pageSize: newSize,
                };
                submitQueryParams({
                  navigate,
                  newValues,
                  parsedSearch,
                  pathname: location.pathname,
                  retry,
                });
              },
              onShowSizeChange: (_currentPage, newSize): void => {
                const newValues = {
                  ...parsedSearch,
                  pageNumber: 1,
                  pageSize: newSize,
                };
                submitQueryParams({
                  navigate,
                  newValues,
                  parsedSearch,
                  pathname: location.pathname,
                  retry,
                });
              },
              pageNumber,
              pageSize,
              showTotal: (total, range): string =>
                t("report.table.showTotal", { range: `${range[0]}-${range[1]}`, total }),
              total: transferRequestList.totalCount,
            }}
          />
        </Flex>
      </SubLayout>
    </Page>
  );
};

export default ShowAllRequestTransferList;
